import i18n from "i18next";
import { makeAutoObservable, runInAction, toJS } from "mobx";
import { v4 as uuidv4 } from "uuid";
import { FilterByOptions } from "../../components/common/BriaTable/BriaTable.tsx";
import { isProduction } from "../../config/env.ts";
import { ADS_EDITOR_IFRAME, CAMPAIGN_API_IFRAME } from "../../constants/IframeConstants.ts";
import { ImageEditingFeatureDetector } from "../../constants/ImageEditingFeatures.ts";
import { pagesSort } from "../../constants/newIframe.ts";
import { PaginatedItems } from "../../models/common.ts";
import { ImageToImageConfigType, PlaygroundImage } from "../../models/image-to-image.ts";
import {
	getDefaultIframe,
	Iframe,
	IframePages,
	IframePostMessageActionTypes,
	IframePostMessageItemTypes,
	IframePostMessageTypes,
} from "../../models/new-iframe.ts";
import { UserOrganization } from "../../models/organization.ts";
import { TextToImageConfigType } from "../../models/text-to-image.ts";
import QueryService from "../../utils/QueryService.ts";
import {
	extractImageToImageConfig,
	extractTextToImageConfig,
	ImageToImagePostMessageConfig,
	TextToImagePostMessageConfig,
} from "./utils/postMessageConfigExtractor.ts";

interface ExportPostMessageItem {
	type: IframePostMessageItemTypes;
	src: string;
	fileName?: string;
}

interface PostMessageInput {
	image_src: string;
	mask_src?: string;
}

interface PostMessageResult {
	type: IframePostMessageItemTypes;
	src: string;
	fileName?: string;
}

interface GeneratePostMessagePayload {
	feature?: string | string[];
	input?: PostMessageInput;
	results: PostMessageResult[];
	config?: ImageToImagePostMessageConfig | TextToImagePostMessageConfig;
	[key: string]: unknown;
}

interface ImageEditingPostMessageParams {
	action: IframePostMessageActionTypes;
	feature: string;
	inputImage: PlaygroundImage;
	resultUrls: string[];
	config: ImageToImageConfigType;
	maskSrc?: string;
}

interface AdsDataQueryParam {
	iframeId?: string;
	organizationId?: string;
	templateId?: string;
	adsIndex?: string;
	brandId?: string;
}

export interface IIframeStore {
	passedImageUrl: string | null;
	queryParams: URLSearchParams;
	iframe?: Iframe;
	iframeForm: Iframe;
	paginatedIframes: PaginatedItems<Iframe>;
	loadingOrgIframes: boolean;
	showIframeAuthErrorPage: boolean;
	rowsPerPage: number;
	userJwtToken: string | null;
	formErrors: { save?: boolean; invalidName?: boolean };
	getOrgIframes: (pageNumber: number, filterBy?: FilterByOptions) => Promise<PaginatedItems<Iframe>>;
	handleFormChange: <K extends keyof Iframe>(key: K, value: Iframe[K]) => void;
	getIframe: (iframeId: string) => Promise<Iframe>;
	getIframeAnonymous: (iframeId: string) => Promise<{ iframe: Iframe; fb_organization: UserOrganization }>;
	createIframe: (iframe: Iframe) => Promise<number>;
	updateIframe: (iframe: Iframe) => Promise<void>;
	deleteIframe: (iframeId: string) => Promise<void>;
	isIframe: () => boolean;
	isPreview: () => boolean;
	sendPostMessage: (type: IframePostMessageTypes, message?: unknown) => void;
	sendExportPostMessage: (
		action: IframePostMessageActionTypes.Save | IframePostMessageActionTypes.Download,
		exportType: IframePostMessageItemTypes,
		items: ExportPostMessageItem[],
		metadata?: Record<string, unknown>,
	) => void;
	sendGeneratePostMessage: (
		config: TextToImageConfigType | ImageToImageConfigType,
		results: PlaygroundImage[],
		isBulkMode?: boolean,
	) => void;
	sendImageEditingPostMessage: (params: ImageEditingPostMessageParams) => void;
	sendUploadPostMessage: (images: PlaygroundImage[]) => void;
	getAdsDataQueryParam: () => AdsDataQueryParam | null;
	setProperty<K extends keyof IframeStore>(key: K, value: IframeStore[K]): void;
}

export class IframeStore implements IIframeStore {
	private queryService: QueryService = new QueryService("/iframes");
	passedImageUrl: string | null;
	iframeSessionId: string;
	showIframeAuthErrorPage: boolean = false;
	isInitialized: boolean = false;
	isIframeConfigLoaded: boolean = false;
	iframe: Iframe;
	iframeForm: Iframe;
	paginatedIframes: PaginatedItems<Iframe> = { total: 0, items: [] };
	rowsPerPage: number = 20;
	userJwtToken: string | null = null;
	loadingOrgIframes: boolean = false;
	formErrors: { save?: boolean; invalidName?: boolean } = {};
	queryParams = new URLSearchParams(location.search);

	constructor() {
		makeAutoObservable(this);
		const defaultIframe = getDefaultIframe("");
		this.iframeSessionId = uuidv4();
		this.iframeForm = defaultIframe;
		this.passedImageUrl = this.queryParams.get("imageUrl");
		this.iframe = {
			...defaultIframe,
			id: (this.getAdsDataQueryParam()?.iframeId as string) ?? this.queryParams.get("iframeId") ?? "",
		};
	}

	setProperty = async <K extends keyof IframeStore>(key: K, value: IframeStore[K]) => {
		runInAction(() => ((this as IframeStore)[key] = value));
	};

	getOrgIframes = async (pageNumber: number, filterBy?: FilterByOptions): Promise<PaginatedItems<Iframe>> => {
		try {
			this.loadingOrgIframes = true;
			const paginatedIframes: PaginatedItems<Iframe> = await this.queryService.get("/", {
				params: {
					filter_by: filterBy,
					page: pageNumber,
					per_page: this.rowsPerPage,
				},
			});

			runInAction(() => {
				this.paginatedIframes = paginatedIframes;
				this.loadingOrgIframes = false;
			});

			return this.paginatedIframes;
		} catch (e: unknown) {
			this.loadingOrgIframes = false;
			const error = e as Error;
			return Promise.reject(`Error loading organization's iframes: ${error.message || String(e)}`);
		}
	};

	handleFormChange = <K extends keyof Iframe>(key: K, value: Iframe[K]) => {
		this.iframeForm = { ...this.iframeForm, [key]: value };
	};

	getIframe = async (iframeId: string): Promise<Iframe> => {
		return this.queryService.get(`/${iframeId}`);
	};

	getIframeAnonymous = async (iframeId: string): Promise<{ iframe: Iframe; fb_organization: UserOrganization }> => {
		return this.queryService.get(`/anonymous/${iframeId}`);
	};

	createIframe = async (iframe: Iframe): Promise<number> => {
		try {
			const iframeId = await this.queryService.post(`/`, iframe);
			return iframeId;
		} catch (e: unknown) {
			const error = e as { response?: { status: number }; message?: string };
			if (error.response?.status === 409) {
				this.formErrors.invalidName = true;
			}
			return Promise.reject(`Error creating new iframe: ${error.message || String(e)}`);
		}
	};

	updateIframe = async (iframe: Iframe): Promise<void> => {
		try {
			await this.queryService.put(`/${iframe.id}`, iframe);
			runInAction(() => {
				this.paginatedIframes.items = this.paginatedIframes?.items.map((template) =>
					template.id === iframe.id ? iframe : template,
				);
			});
		} catch (e: unknown) {
			const error = e as { response?: { status: number }; message?: string };
			if (error.response?.status === 409) {
				this.formErrors.invalidName = true;
			}
			return Promise.reject(`Error updating iframe: ${error.message || String(e)}`);
		}
	};

	deleteIframe = async (iframeId: string): Promise<void> => {
		return this.queryService.delete(`/${iframeId}`);
	};

	isIframe = (): boolean => {
		return !!this.iframe?.id;
	};

	isPreview = (): boolean => {
		return this.isIframe() && this.iframe?.id === "preview";
	};

	isCampaignApi = (): boolean => {
		return this.isIframe() && this.iframe?.id === CAMPAIGN_API_IFRAME;
	};

	isAdsEditorIframe = (): boolean => {
		return this.isIframe() && this.iframe?.id === ADS_EDITOR_IFRAME;
	};

	sendPostMessage = (type: IframePostMessageTypes, message: unknown = {}) => {
		if (this.isIframe()) {
			const messageJson = {
				iframeId: this.iframe.id,
				sessionId: this.iframeSessionId,
				type,
				message: toJS(message),
			};

			!isProduction() && console.log("iFrame postMessage sent to *", messageJson);
			window.parent.postMessage(messageJson, "*");
		}
	};

	sendActionPostMessage = (
		action: IframePostMessageActionTypes,
		additionalData?: GeneratePostMessagePayload | Record<string, unknown>,
	) => {
		this.sendPostMessage(IframePostMessageTypes.Action, {
			page: this.getIframeAppName(iframeStore.iframe.config.landing_page),
			action,
			...additionalData,
		});
	};

	sendExportPostMessage = (
		action: IframePostMessageActionTypes.Save | IframePostMessageActionTypes.Download,
		exportType: IframePostMessageItemTypes,
		items: ExportPostMessageItem[],
		metadata?: Record<string, unknown>,
	) => {
		this.sendActionPostMessage(action, {
			type: exportType,
			items,
			metadata,
		});
	};

	sendGeneratePostMessage = (
		config: TextToImageConfigType | ImageToImageConfigType,
		results: PlaygroundImage[],
		isBulkMode?: boolean,
	) => {
		const plainConfig = toJS(config);
		const payload: GeneratePostMessagePayload = {
			results: results.map((image) => ({ type: IframePostMessageItemTypes.Image, src: image.url })),
		};

		if ("original_image" in plainConfig) {
			payload.feature = isBulkMode
				? ImageEditingFeatureDetector.detectBulkModeFeaturesArray(plainConfig)
				: ImageEditingFeatureDetector.detectSingleModeFeature(plainConfig);

			const extractedConfig = extractImageToImageConfig(plainConfig);
			if (Object.keys(extractedConfig).length > 0) {
				payload.config = extractedConfig;
			}

			payload.input = { image_src: plainConfig.original_image?.url };
			const isObjectManipulation =
				plainConfig.object_manipulation?.selected &&
				(plainConfig.object_manipulation?.erase || plainConfig.object_manipulation?.gen_fill);
			if (isObjectManipulation && plainConfig.object_manipulation?.mask) {
				payload.input.mask_src = plainConfig.object_manipulation.mask;
			}
		} else {
			const extractedConfig = extractTextToImageConfig(plainConfig);
			if (Object.keys(extractedConfig).length > 0) {
				payload.config = extractedConfig;
			}
		}

		this.sendActionPostMessage(IframePostMessageActionTypes.Generate, payload);
	};

	sendImageEditingPostMessage = ({
		action,
		feature,
		inputImage,
		resultUrls,
		config,
		maskSrc,
	}: ImageEditingPostMessageParams) => {
		const plainConfig = toJS(config);
		const extractedConfig = extractImageToImageConfig(plainConfig);

		const payload: GeneratePostMessagePayload = {
			feature,
			input: {
				image_src: inputImage.url,
				...(maskSrc && { mask_src: maskSrc }),
			},
			results: resultUrls.map((url) => ({ type: IframePostMessageItemTypes.Image, src: url })),
		};

		if (Object.keys(extractedConfig).length > 0) {
			payload.config = extractedConfig;
		}

		this.sendActionPostMessage(action, payload);
	};

	sendUploadPostMessage = (images: PlaygroundImage[]) => {
		this.sendActionPostMessage(IframePostMessageActionTypes.Upload, {
			type: IframePostMessageItemTypes.Image,
			items: images.map((image) => ({ type: IframePostMessageItemTypes.Image, src: image.url })),
		});
	};

	getIframeAppName = (app: IframePages) => {
		return i18n.t(`staticDropdowns.appPages.${app}`);
	};

	sortPages = (page1: string, page2: string) => {
		return pagesSort.indexOf(page1 as IframePages) - pagesSort.indexOf(page2 as IframePages);
	};

	getAdsDataQueryParam = (): AdsDataQueryParam | null => {
		const adsDataParam = this.queryParams.get("adsData");

		if (adsDataParam) {
			try {
				const decodedJson = decodeURIComponent(atob(adsDataParam));
				return JSON.parse(decodedJson) as AdsDataQueryParam;
			} catch (e) {
				console.error("Failed to decode adsData", e);
			}
		}
		return null;
	};
}

const iframeStore = new IframeStore();
export default iframeStore;
